import React, { useState, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ paymentIntentId, amount, toUserName, onSuccess, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message);
        setIsProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        const confirmRes = await fetch(`${BACKEND_URL}/api/payments/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ payment_intent_id: paymentIntentId }),
        });

        if (confirmRes.ok) {
          const data = await confirmRes.json();
          if (data.status === 'succeeded' || data.status === 'already_processed') {
            toast.success(`Paid $${(amount / 100).toFixed(2)} to ${toUserName}`);
            onSuccess();
          } else {
            toast.info('Payment processing... balances will update shortly.');
            onSuccess();
          }
        } else {
          toast.error('Payment succeeded but confirmation failed. Balances will update shortly.');
          onSuccess();
        }
      } else {
        setErrorMessage('Payment could not be completed. Please try again.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setErrorMessage('An unexpected error occurred.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="text-center pt-1">
        <p className="text-xs uppercase tracking-wider text-gray-500">Paying</p>
        <p className="text-3xl font-bold text-gray-900 tabular-nums mt-1">${(amount / 100).toFixed(2)}</p>
        <p className="text-sm text-gray-500 mt-0.5">to {toUserName}</p>
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2 font-medium">Payment method</p>
        <div className="rounded-lg border border-gray-200 p-3 bg-white">
          <PaymentElement options={{ layout: { type: 'tabs', defaultCollapsed: false } }} />
        </div>
      </div>

      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="payment-error">
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isProcessing}
          className="flex-1 h-11"
          data-testid="payment-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isProcessing || !stripe || !elements}
          className="flex-[2] h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
          data-testid="payment-submit-btn"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing…
            </>
          ) : (
            <>Pay ${(amount / 100).toFixed(2)}</>
          )}
        </Button>
      </div>
    </form>
  );
};

const PaymentModal = ({ isOpen, onClose, groupId, toUserId, toUserName, amountCents, onSuccess }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  React.useEffect(() => {
    if (isOpen && !clientSecret) {
      createPaymentIntent();
    }
    if (!isOpen) {
      setClientSecret(null);
      setPaymentIntentId(null);
      setCreateError(null);
    }
  }, [isOpen]);

  const createPaymentIntent = async () => {
    setIsCreating(true);
    setCreateError(null);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/groups/${groupId}/payments/create-intent`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ to_user_id: toUserId, amount_cents: amountCents }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setClientSecret(data.client_secret);
        setPaymentIntentId(data.payment_intent_id);
      } else {
        const error = await response.json();
        setCreateError(error.detail || 'Failed to create payment');
      }
    } catch (error) {
      console.error('Error creating payment intent:', error);
      setCreateError('Failed to connect to payment service');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  const elementsOptions = clientSecret ? {
    clientSecret,
    appearance: { theme: 'stripe', variables: { colorPrimary: '#2563eb', borderRadius: '8px' } },
  } : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="payment-modal">
      <Card className="max-w-md w-full max-h-[92vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base font-semibold">
            Pay {toUserName}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="payment-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {isCreating ? (
            <div className="flex flex-col items-center justify-center py-12" data-testid="payment-loading">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-3" />
              <p className="text-sm text-gray-500">Setting up payment...</p>
            </div>
          ) : createError ? (
            <div className="space-y-4" data-testid="payment-create-error">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{createError}</p>
              </div>
              <div className="flex space-x-3">
                <Button variant="outline" onClick={onClose} className="flex-1">Close</Button>
                <Button onClick={createPaymentIntent} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">Retry</Button>
              </div>
            </div>
          ) : clientSecret ? (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <CheckoutForm
                paymentIntentId={paymentIntentId}
                amount={amountCents}
                toUserName={toUserName}
                onSuccess={() => { onClose(); onSuccess(); }}
                onClose={onClose}
              />
            </Elements>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentModal;
